import {
  Globe,
  Users,
  Shield,
  CreditCard,
  Bell,
  Edit3,
  Trash2,
} from "lucide-react";
import { ProductListConfig, Button, DyanamicField } from "@b2b/ui-components";

export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  status: "active" | "inactive";
  lastLogin: string;
}

export interface Permission {
  id: string;
  name: string;
  description: string;
  category: string;
}

export const users: User[] = [
  {
    id: "1",
    name: "John Admin",
    email: "john@marketplace.com",
    role: "Admin",
    status: "active",
    lastLogin: "2024-01-15 14:30",
  },
  {
    id: "2",
    name: "Sarah Manager",
    email: "sarah@marketplace.com",
    role: "Manager",
    status: "active",
    lastLogin: "2024-01-15 10:15",
  },
  {
    id: "3",
    name: "Mike Viewer",
    email: "mike@marketplace.com",
    role: "Viewer",
    status: "inactive",
    lastLogin: "2024-01-12 16:45",
  },
];

export const permissions: Permission[] = [
  {
    id: "1",
    name: "View Dashboard",
    description: "Access to main dashboard",
    category: "General",
  },
  {
    id: "2",
    name: "Manage Users",
    description: "Create, edit, delete users",
    category: "User Management",
  },
  {
    id: "3",
    name: "Manage Products",
    description: "Full product management access",
    category: "Product Management",
  },
  {
    id: "4",
    name: "View Reports",
    description: "Access to analytics and reports",
    category: "Reports",
  },
  {
    id: "5",
    name: "System Settings",
    description: "Configure system settings",
    category: "Administration",
  },
];

export const tabsConfig = [
  { value: "general", label: "General", icon: Globe },
  { value: "users", label: "User Management", icon: Users },
  { value: "permissions", label: "Permissions", icon: Shield },
  { value: "billing", label: "Payment & Billing", icon: CreditCard },
  { value: "notifications", label: "Notifications", icon: Bell },
];

export const AdminuserTableConfig: ProductListConfig = {
  columns: [
    {
      key: "name",
      label: "User",
      render: (row) => (
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary-light rounded-lg flex items-center justify-center">
            <span className="text-primary font-medium text-sm">
              {row.name
                .split(" ")
                .map((n: string) => n[0])
                .join("")}
            </span>
          </div>
          <div>
            <p className="font-medium">{row.name}</p>
            <p className="text-sm text-muted-foreground">{row.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: "role",
      label: "Role",
      render: (row) => (
        <span className="px-2 py-1 border rounded text-sm">{row.role}</span>
      ),
    },
    {
      key: "status",
      label: "Status",
    },
    {
      key: "lastLogin",
      label: "Last Login",
    },
    {
      key: "actions",
      label: "Actions",
      align: "right",
      render: () => (
        <div className="flex items-center justify-end space-x-2">
          <Button variant="ghost" size="sm">
            <Edit3 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ],
  styles: {
    headerBg: "bg-gray-100",
    headerText: "text-gray-700",
    rowHover: "hover:bg-gray-50",
    statusStyles: {
      active: { background: "bg-green-100", textColor: "text-green-800" },
      inactive: { background: "bg-gray-200", textColor: "text-gray-700" },
    },
  },
};

export const generalFields: DyanamicField[] = [
  {
    key: "companyName",
    label: "Company Name",
    type: "input",
    placeholder: "MarketPlace Inc.",
    required: true,
  },
  {
    key: "companyEmail",
    label: "Contact Email",
    type: "input",
    placeholder: "contact@marketplace.com",
    required: true,
  },
  {
    key: "companyAddress",
    label: "Address",
    type: "textarea",
    placeholder: "123 Business St, City, Country",
  },
];

export const regionalFields: DyanamicField[] = [
  {
    key: "timezone",
    label: "Timezone",
    type: "dropdown",
    options: ["UTC", "Eastern Time", "Pacific Time", "GMT"],
    placeholder: "Select timezone",
  },
  {
    key: "currency",
    label: "Default Currency",
    type: "dropdown",
    options: ["USD ($)", "EUR (€)", "GBP (£)", "CNY (¥)"],
    placeholder: "Select currency",
  },
  {
    key: "language",
    label: "Language",
    type: "dropdown",
    options: ["English", "Spanish", "French", "German"],
    placeholder: "Select language",
  },
];

export const brandingFields: DyanamicField[] = [
  {
    key: "companyLogo",
    label: "Company Logo",
    type: "file",
    accept: { "image/*": [".png", ".jpg", ".jpeg"] },
  },
  {
    key: "primaryColor",
    label: "Primary Color",
    type: "input",
    placeholder: "Enter primary color (e.g., #FF6B35)",
  },
  {
    key: "secondaryColor",
    label: "Secondary Color",
    type: "input",
    placeholder: "Enter secondary color (e.g., #F5F5F5)",
  },
];
